import { Link } from "react-router-dom";
import { ArrowRight, Zap, Link2 } from "lucide-react";
import { GREEN, BLUE, ORANGE, Wordmark, EvaMark } from "./shared";
import { FooterLegal } from "@/components/public/FooterLegal";

export function PreviewSection() {
  return (
    <section className="py-20 lg:py-24" style={{ background: "#0F2A1E" }} data-testid="section-preview">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="lp-reveal text-center max-w-2xl mx-auto mb-12">
          <h2 className="text-2xl md:text-4xl font-extrabold tracking-tight text-white" style={{ fontFamily: "Manrope, sans-serif" }}>
            La tua ESG, in un colpo d'occhio.
          </h2>
        </div>
        <div className="lp-reveal rounded-2xl overflow-hidden shadow-2xl border border-white/10"
          role="img" aria-label="Anteprima completa della dashboard ESG-PMI di Alfa Srl con VSME 87%, PMI-Banche 91%, Environmental 72%, Social 94%, Governance 68%">
          <div className="px-5 py-3 flex items-center justify-between" style={{ background: BLUE }}>
            <div className="flex items-center gap-3">
              <Wordmark compact />
              <span className="text-xs text-white/70 font-medium">Alfa Srl · VSME Basic</span>
            </div>
            <span className="text-[10px] font-bold px-2.5 py-1 rounded-full text-white" style={{ background: GREEN }}>Esercizio 2026</span>
          </div>
          <div className="bg-white p-6">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-5">
              {[
                ["VSME Basic", 87, GREEN], ["PMI-Banche", 91, BLUE], ["Environmental", 72, "#1E9E76"],
                ["Social", 94, "#2F5D8A"], ["Governance", 68, "#5B7CA3"],
              ].map(([l, p, c]) => (
                <div key={l} className="rounded-xl border border-slate-200 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{l}</p>
                  <p className="text-2xl font-extrabold mt-1" style={{ color: c, fontFamily: "Manrope, sans-serif" }}>{p}%</p>
                  <div className="h-1.5 rounded-full bg-slate-100 mt-2">
                    <div className="h-full rounded-full lp-progress" style={{ width: `${p}%`, background: c }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 flex items-center gap-2.5">
                <Zap className="w-4 h-4 text-amber-700" aria-hidden="true" />
                <p className="text-xs font-semibold text-amber-900">Dati mancanti: 6 · pronti da completare</p>
              </div>
              <div className="rounded-lg px-4 py-3 flex items-center gap-2.5" style={{ background: "#E7F4EE" }}>
                <Link2 className="w-4 h-4" style={{ color: GREEN }} aria-hidden="true" />
                <p className="text-xs font-semibold" style={{ color: GREEN }}>JOBTIMEHR: sincronizzato</p>
              </div>
              <div className="rounded-lg px-4 py-3 flex items-center gap-2.5" style={{ background: "#EFF4FA" }}>
                <EvaMark size={20} />
                <p className="text-xs font-semibold" style={{ color: BLUE }}>Eva: "Cosa vuoi sapere?"</p>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-slate-400 italic">Esempio di dashboard — dati dimostrativi.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function CtaFinaleSection() {
  return (
    <section className="py-20 lg:py-28" style={{ background: `linear-gradient(120deg, ${GREEN} 0%, #14564A 45%, ${BLUE} 100%)` }}
      data-testid="section-cta-finale">
      <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center lp-reveal">
        <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight text-white leading-tight"
          style={{ fontFamily: "Manrope, sans-serif" }} data-testid="cta-final-title">
          Il tuo ESG può essere molto più semplice.
        </h2>
        <p className="mt-5 text-base md:text-lg text-white/85 leading-relaxed max-w-2xl mx-auto">
          Raccogli i dati una volta. ESG-PMI li trasforma in informazioni utilizzabili
          per impresa, banche, clienti e filiere.
        </p>
        <div className="mt-9 flex flex-wrap justify-center gap-4">
          <Link to="/richiedi-demo"
            className="inline-flex items-center gap-2 text-sm font-bold px-8 py-4 rounded-md transition-transform hover:-translate-y-0.5"
            style={{ background: ORANGE, color: BLUE }} data-testid="cta-demo-final">
            Richiedi una demo <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
          <Link to="/richiedi-demo"
            className="inline-flex items-center gap-2 text-sm font-bold px-8 py-4 rounded-md border-2 border-white/70 text-white hover:bg-white/10 transition-colors"
            data-testid="cta-contact-final">
            Parla con noi
          </Link>
        </div>
      </div>
    </section>
  );
}

export function LandingFooter() {
  return (
    <footer id="contatti" className="scroll-mt-20" style={{ background: "#0C2540" }} data-testid="landing-footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
        <div className="grid md:grid-cols-4 gap-10">
          <div>
            <span className="inline-block bg-white rounded-lg px-3 py-1.5"><Wordmark compact /></span>
            <p className="mt-3 text-xs text-white/60 leading-relaxed">
              La piattaforma ESG per micro, piccole e medie imprese. Integrata con JOBTIMEHR.
            </p>
          </div>
          {[
            { t: "Prodotto", links: [["VSME", "/vsme"], ["PMI-Banche", "/pmi-banche"], ["Environmental", "/environmental"], ["Social", "/social"], ["Governance", "/governance"], ["Carbon Calculator", "/carbon-calculator"], ["Eva", "/eva"]] },
            { t: "Soluzioni", links: [["PMI", "/pmi"], ["Consulenti", "/consulenti"], ["JOBTIMEHR", "/jobtimehr"], ["Cassetto ESG", "/cassetto-esg"]] },
            { t: "Azienda", links: [["Chi siamo", "/chi-siamo"], ["Guide", "/guide"], ["Richiedi una demo", "/richiedi-demo"], ["Privacy", "/privacy"], ["Termini", "/termini"]] },
          ].map(({ t, links }) => (
            <nav key={t} aria-label={`Footer ${t}`}>
              <p className="text-xs font-bold uppercase tracking-wider text-white/50 mb-3">{t}</p>
              <ul className="space-y-2">
                {links.map(([label, href]) => (
                  <li key={label}>
                    <Link to={href} className="text-sm text-white/75 hover:text-white transition-colors"
                      data-testid={`footer-${label.toLowerCase().replace(/[^a-z]/g, "")}`}>{label}</Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>
        <FooterLegal />
      </div>
    </footer>
  );
}
